import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { employeeAPI } from '../../services/api';
import EmployeePayroll from './EmployeePayroll';
import {
  ArrowLeftIcon,
  PencilIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BriefcaseIcon,
  CurrencyRupeeIcon,
  CalendarIcon,
  MapPinIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');

  // Fetch employee details
  const { data: employee, isLoading, error } = useQuery({
    queryKey: ['employee', id],
    queryFn: async () => {
      const response = await employeeAPI.getById(id);
      return response.data;
    },
  });

  const tabs = [
    { id: 'details', name: 'Details', icon: <UserIcon className="w-4 h-4" /> },
    { id: 'payroll', name: 'Payroll', icon: <CurrencyRupeeIcon className="w-4 h-4" /> },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 text-center">
            <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Error Loading Employee</h2>
            <p className="text-gray-300 mb-4">{error.message || 'Failed to load employee details'}</p>
            <button
              onClick={() => navigate('/employees')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Employees
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gray-900 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-white mb-2">Employee Not Found</h2>
            <button
              onClick={() => navigate('/employees')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Employees
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-6">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/employees')}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {employee.first_name} {employee.last_name}
                </h1>
                <p className="text-gray-400">{employee.designation}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  employee.is_active
                    ? 'bg-green-900/50 text-green-400 border border-green-500'
                    : 'bg-red-900/50 text-red-400 border border-red-500'
                }`}
              >
                {employee.is_active ? 'Active' : 'Inactive'}
              </span>
              <Link
                to={`/employees/${id}/edit`}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PencilIcon className="w-4 h-4" />
                <span>Edit</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2`}
                >
                  {tab.icon}
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
              <UserIcon className="w-5 h-5 mr-2 text-blue-500" />
              Employee Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Employee ID */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Employee ID
                </label>
                <p className="text-blue-400 font-mono text-lg font-semibold">
                  {employee.employee_id}
                </p>
              </div>

              {/* Email */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1 flex items-center">
                  <EnvelopeIcon className="w-4 h-4 mr-1" />
                  Email
                </label>
                <p className="text-white">{employee.email}</p>
              </div>

              {/* Phone */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1 flex items-center">
                  <PhoneIcon className="w-4 h-4 mr-1" />
                  Phone
                </label>
                <p className="text-white">{employee.phone}</p>
              </div>

              {/* Department */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1 flex items-center">
                  <BriefcaseIcon className="w-4 h-4 mr-1" />
                  Department
                </label>
                <p className="text-white">{employee.department || 'N/A'}</p>
              </div>

              {/* Designation */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Designation
                </label>
                <p className="text-white">{employee.designation}</p>
              </div>

              {/* Salary */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1 flex items-center">
                  <CurrencyRupeeIcon className="w-4 h-4 mr-1" />
                  Monthly Salary
                </label>
                <p className="text-green-400 text-lg font-semibold">
                  {formatCurrency(employee.salary)}
                </p>
              </div>

              {/* Date of Joining */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1 flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Date of Joining
                </label>
                <p className="text-white">{formatDate(employee.date_of_joining)}</p>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Emergency Contact
                </label>
                <p className="text-white">{employee.emergency_contact || 'N/A'}</p>
              </div>

              {/* Address - Full Width */}
              <div className="bg-gray-700/50 p-4 rounded-lg md:col-span-2">
                <label className="block text-sm font-medium text-gray-400 mb-1 flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  Address
                </label>
                <p className="text-white">{employee.address || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payroll' && (
          <EmployeePayroll
            employeeId={employee.employee_id}
            employeeName={`${employee.first_name} ${employee.last_name}`}
            salary={employee.salary}
          />
        )}
      </div>
    </div>
  );
};

export default EmployeeDetail;
